import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DashboardLayout from '../components/DashboardLayout';
import {
  confirmAdminBooking,
  createAdminBookingPaymentLink,
  fetchAdminBooking,
  fetchAdminBookings,
  markAdminBookingPaid,
  markAdminBookingPaymentFailed,
  markAdminBookingPaymentPending,
  rejectAdminBooking,
} from '../services/booking';

const BOOKING_STATUS_OPTIONS = [
  { value: '', label: 'All booking statuses' },
  { value: 'tutor_selected', label: 'Tutor Selected' },
  { value: 'payment_pending', label: 'Payment Pending' },
  { value: 'pending_admin_confirmation', label: 'Awaiting Tutor Response' },
  { value: 'tutor_response_overdue', label: 'Tutor No Response' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'rejected', label: 'Rejected' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: 'All payment statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'checkout_created', label: 'Gateway Checkout Created' },
  { value: 'checkout_pending', label: 'Gateway Checkout Pending' },
  { value: 'submitted_manual', label: 'Submitted Manual' },
  { value: 'paid_manual', label: 'Paid Manual' },
  { value: 'paid_gateway', label: 'Paid By Gateway' },
  { value: 'paid_mock', label: 'Paid Mock' },
  { value: 'uncertain', label: 'Uncertain' },
  { value: 'failed', label: 'Failed' },
];

const DELIVERY_MODE_OPTIONS = [
  { value: '', label: 'All delivery modes' },
  { value: 'online', label: 'Online' },
  { value: 'in-person', label: 'In-Person' },
];

const CURRICULUM_OPTIONS = [
  { value: '', label: 'All curricula' },
  { value: 'CAPS', label: 'CAPS' },
  { value: 'IEB', label: 'IEB' },
  { value: 'CAMBRIDGE', label: 'Cambridge' },
  { value: 'IB', label: 'IB' },
  { value: 'PEARSON EDEXEL', label: 'Pearson Edexcel' },
];

const PROVINCE_OPTIONS = [
  { value: '', label: 'All provinces' },
  { value: 'Gauteng', label: 'Gauteng' },
  { value: 'Western Cape', label: 'Western Cape' },
  { value: 'KwaZulu-Natal', label: 'KwaZulu-Natal' },
  { value: 'Eastern Cape', label: 'Eastern Cape' },
  { value: 'Free State', label: 'Free State' },
  { value: 'Limpopo', label: 'Limpopo' },
  { value: 'Mpumalanga', label: 'Mpumalanga' },
  { value: 'North West', label: 'North West' },
  { value: 'Northern Cape', label: 'Northern Cape' },
];

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const formatAdminMoney = (amount, currency = 'ZAR') => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '-';
  const code = String(currency || 'ZAR').toUpperCase();
  if (code === 'ZAR') return `R${Math.round(numeric).toLocaleString('en-ZA')}`;
  if (code === 'USD') return `$${numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${code} ${numeric.toLocaleString('en-ZA')}`;
};

const formatPerson = (user) => {
  if (!user) return '-';
  const full = [user.name, user.surname].filter(Boolean).join(' ').trim();
  return full || user.email || '-';
};

const formatGradeLabel = (grade) => {
  if (grade === 'R') return 'Grade R';
  const numeric = Number(grade);
  if (numeric === 0) return 'Grade R';
  if (Number.isFinite(numeric) && numeric > 0) return `Grade ${numeric}`;
  if (String(grade || '').trim()) return `Grade ${grade}`;
  return '-';
};

const formatDeliveryMode = (value) =>
  String(value || '-')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatList = (value) => {
  if (!Array.isArray(value)) return '-';
  const items = value.filter(Boolean);
  return items.length ? items.join(', ') : '-';
};

const formatAvailability = (availability) => {
  if (!availability || typeof availability !== 'object') return 'Not set';
  const days = Array.isArray(availability.daysAvailable) ? availability.daysAvailable.filter(Boolean) : [];
  const slots = Array.isArray(availability.timeSlots) ? availability.timeSlots.filter(Boolean) : [];
  const labels = [];
  if (days.length) labels.push(days.join(', '));
  if (slots.length) labels.push(slots.join(', '));
  if (availability.timeZone) labels.push(availability.timeZone);
  if (availability.hasWeekendAvailability) labels.push('Weekend support');
  if (availability.hasEmergencyExamSupport) labels.push('Emergency exam support');
  return labels.length ? labels.join(' · ') : 'Not set';
};

const statusLabel = (value) => {
  switch (String(value || '').toLowerCase()) {
    case 'payment_pending':
      return 'Payment Pending';
    case 'tutor_selected':
      return 'Tutor Selected';
    case 'pending_admin_confirmation':
      return 'Awaiting Tutor Response';
    case 'tutor_response_overdue':
      return 'Tutor No Response';
    case 'submitted_manual':
      return 'Manual Payment Submitted';
    case 'checkout_created':
      return 'Gateway Checkout Created';
    case 'checkout_pending':
      return 'Gateway Checkout Pending';
    case 'paid_manual':
      return 'Paid Manually';
    case 'paid_gateway':
      return 'Paid By Gateway';
    case 'paid_mock':
      return 'Paid (Mock)';
    case 'refunded_gateway':
      return 'Refunded By Gateway';
    case 'not_started':
      return 'Not Started';
    default:
      return String(value || '-')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
  }
};

const timelineLabel = (entry) => entry?.label || statusLabel(entry?.type);

const statusColor = (value) => {
  switch (String(value || '').toLowerCase()) {
    case 'confirmed':
    case 'paid_manual':
    case 'paid_gateway':
    case 'paid_mock':
    case 'accepted':
      return 'success';
    case 'rejected':
    case 'failed':
      return 'error';
    case 'pending_admin_confirmation':
    case 'tutor_response_overdue':
    case 'checkout_created':
    case 'checkout_pending':
    case 'submitted_manual':
    case 'uncertain':
      return 'warning';
    default:
      return 'default';
  }
};

const timelineTone = (value) => {
  switch (value) {
    case 'success':
      return 'success.main';
    case 'error':
      return 'error.main';
    case 'warning':
      return 'warning.main';
    default:
      return 'text.secondary';
  }
};

const PAID_PAYMENT_STATUSES = ['paid_manual', 'paid_gateway', 'paid_mock'];
const INACTIVE_PAYMENT_LINK_BOOKING_STATUSES = ['confirmed', 'rejected', 'cancelled'];

const getBookingCurrency = (booking) =>
  String(booking?.billingCurrency || booking?.pricingSnapshot?.currency || '').toUpperCase();

const defaultPaymentProvider = (booking) => (getBookingCurrency(booking) === 'ZAR' ? 'yoco' : 'paypal');

const paymentLinkForPayment = (payment) =>
  payment?.link ||
  payment?.payload?.paypalApprovalUrl ||
  payment?.payload?.yocoRedirectUrl ||
  '';

const defaultPaymentAmount = (booking, provider) => {
  const targetCurrency = provider === 'paypal' ? 'USD' : 'ZAR';
  if (getBookingCurrency(booking) !== targetCurrency) return '';
  const value = Number(
    booking?.pricingSnapshot?.totalClientAmount ??
    booking?.pricingSnapshot?.clientRate ??
    0
  );
  return Number.isFinite(value) && value > 0 ? value.toFixed(2) : '';
};

const canCreatePaymentLink = (booking) => {
  if (!booking?._id) return false;
  if (INACTIVE_PAYMENT_LINK_BOOKING_STATUSES.includes(String(booking.bookingStatus))) return false;
  if (PAID_PAYMENT_STATUSES.includes(String(booking.paymentStatus))) return false;
  return true;
};

const Bookings = () => {
  const [bookingStatus, setBookingStatus] = useState('pending_admin_confirmation');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('');
  const [curriculum, setCurriculum] = useState('');
  const [province, setProvince] = useState('');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [bookings, setBookings] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', booking: null });
  const [actionNote, setActionNote] = useState('');
  const [actionReference, setActionReference] = useState('');
  const [actionMethod, setActionMethod] = useState('manual_transfer');
  const [actionEvidenceSource, setActionEvidenceSource] = useState('bank_reference');
  const [submitting, setSubmitting] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailBooking, setDetailBooking] = useState(null);
  const [paymentLinkDialog, setPaymentLinkDialog] = useState({ open: false, booking: null });
  const [paymentLinkProvider, setPaymentLinkProvider] = useState('yoco');
  const [paymentLinkAmount, setPaymentLinkAmount] = useState('');
  const [paymentLinkNote, setPaymentLinkNote] = useState('');
  const [paymentLinkSubmitting, setPaymentLinkSubmitting] = useState(false);
  const [paymentLinkError, setPaymentLinkError] = useState('');
  const [latestPaymentLink, setLatestPaymentLink] = useState('');
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, booking: null });

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchAdminBookings({
        bookingStatus: bookingStatus || undefined,
        paymentStatus: paymentStatus || undefined,
        deliveryMode: deliveryMode || undefined,
        curriculum: curriculum || undefined,
        province: province || undefined,
        q: query || undefined,
        limit: 100,
      });
      setBookings(Array.isArray(data?.items) ? data.items : []);
      setSummaryData(data?.summary || null);
    } catch (err) {
      setBookings([]);
      setError(err?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [bookingStatus, curriculum, deliveryMode, paymentStatus, province, query]);

  useEffect(() => {
    loadBookings();
  }, [loadBookings]);

  const summary = useMemo(() => {
    if (summaryData?.totals) {
      return {
        awaiting: Number(summaryData.totals.awaitingAdmin || 0),
        confirmed: Number(summaryData.totals.confirmed || 0),
        rejected: Number(summaryData.totals.rejected || 0),
        paid: Number(summaryData.totals.paid || 0),
      };
    }
    return bookings.reduce(
      (acc, booking) => {
        const status = String(booking?.bookingStatus || '');
        const payment = String(booking?.paymentStatus || '');
        if (status === 'pending_admin_confirmation') acc.awaiting += 1;
        if (status === 'confirmed') acc.confirmed += 1;
        if (status === 'rejected') acc.rejected += 1;
        if (payment === 'paid_mock') acc.paid += 1;
        if (payment === 'paid_manual') acc.paid += 1;
        if (payment === 'paid_gateway') acc.paid += 1;
        return acc;
      },
      { awaiting: 0, confirmed: 0, rejected: 0, paid: 0 }
    );
  }, [bookings, summaryData]);

  const openAction = (type, booking) => {
    setActionDialog({ open: true, type, booking });
    setActionNote('');
    setActionReference('');
    setActionMethod('manual_transfer');
    setActionEvidenceSource('bank_reference');
  };

  const closeAction = () => {
    if (submitting) return;
    setActionDialog({ open: false, type: '', booking: null });
    setActionNote('');
    setActionReference('');
    setActionMethod('manual_transfer');
    setActionEvidenceSource('bank_reference');
  };

  const openDetail = async (booking) => {
    if (!booking?._id) return;
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setError('');
      const data = await fetchAdminBooking(booking._id);
      setDetailBooking(data);
    } catch (err) {
      setError(err?.message || 'Failed to load booking detail');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailBooking(null);
    setDetailLoading(false);
  };

  const openActionMenu = (event, booking) => {
    setActionMenu({ anchorEl: event.currentTarget, booking });
  };

  const closeActionMenu = () => {
    setActionMenu({ anchorEl: null, booking: null });
  };

  const handleMenuAction = (type) => {
    const booking = actionMenu.booking;
    closeActionMenu();
    if (!booking) return;
    if (type === 'view') openDetail(booking);
    else openAction(type, booking);
  };

  const openPaymentLinkDialog = (booking) => {
    const provider = defaultPaymentProvider(booking);
    setPaymentLinkDialog({ open: true, booking });
    setPaymentLinkProvider(provider);
    setPaymentLinkAmount(defaultPaymentAmount(booking, provider));
    setPaymentLinkNote('');
    setPaymentLinkError('');
    setLatestPaymentLink('');
  };

  const closePaymentLinkDialog = () => {
    if (paymentLinkSubmitting) return;
    setPaymentLinkDialog({ open: false, booking: null });
    setPaymentLinkError('');
    setPaymentLinkNote('');
    setLatestPaymentLink('');
  };

  const changePaymentLinkProvider = (provider) => {
    setPaymentLinkProvider(provider);
    setPaymentLinkAmount(defaultPaymentAmount(paymentLinkDialog.booking, provider));
  };

  const copyPaymentLink = async (link) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch (err) {
      const input = document.createElement('textarea');
      input.value = link;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
  };

  const submitPaymentLink = async () => {
    const bookingId = paymentLinkDialog.booking?._id;
    if (!bookingId) return;
    const amount = Number(paymentLinkAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentLinkError('Enter a valid amount greater than zero.');
      return;
    }

    try {
      setPaymentLinkSubmitting(true);
      setPaymentLinkError('');
      const result = await createAdminBookingPaymentLink(bookingId, {
        provider: paymentLinkProvider,
        amount,
        note: paymentLinkNote.trim(),
      });
      const link = result?.checkout?.actionUrl || result?.payment?.link || '';
      setLatestPaymentLink(link);
      setPaymentLinkDialog((current) => ({ ...current, booking: result?.booking || current.booking }));
      if (detailBooking && String(detailBooking._id) === String(bookingId)) {
        setDetailBooking(result?.booking || detailBooking);
      }
      await loadBookings();
    } catch (err) {
      setPaymentLinkError(err?.message || 'Failed to create payment link');
    } finally {
      setPaymentLinkSubmitting(false);
    }
  };

  const submitAction = async () => {
    if (!actionDialog.booking?._id) return;
    if (actionDialog.type === 'reject' && !actionNote.trim()) {
      setError('A rejection reason is required.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      if (actionDialog.type === 'confirm') {
        const result = await confirmAdminBooking(actionDialog.booking._id, actionNote.trim());
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      } else if (actionDialog.type === 'markPaid') {
        const result = await markAdminBookingPaid(actionDialog.booking._id, {
          note: actionNote.trim(),
          reference: actionReference.trim(),
          paymentMethod: actionMethod,
          evidenceSource: actionEvidenceSource,
          evidenceNote: actionNote.trim(),
        });
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      } else if (actionDialog.type === 'markPending') {
        const result = await markAdminBookingPaymentPending(actionDialog.booking._id, {
          note: actionNote.trim(),
        });
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      } else if (actionDialog.type === 'markFailed') {
        const result = await markAdminBookingPaymentFailed(actionDialog.booking._id, {
          reason: actionNote.trim(),
        });
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      } else {
        const result = await rejectAdminBooking(actionDialog.booking._id, actionNote.trim());
        if (detailBooking && String(detailBooking._id) === String(actionDialog.booking._id)) {
          setDetailBooking(result?.booking || detailBooking);
        }
      }
      closeAction();
      await loadBookings();
    } catch (err) {
      setError(err?.message || 'Failed to update booking');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack className={`booking-filter-toolbar${filtersExpanded ? ' is-expanded' : ''}`} direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
              <FormControl fullWidth sx={{ minWidth: { md: 220 }, flex: { md: '1 1 220px' } }}>
                <InputLabel id="booking-status-label">Booking Status</InputLabel>
                <Select
                  labelId="booking-status-label"
                  value={bookingStatus}
                  label="Booking Status"
                  onChange={(e) => setBookingStatus(e.target.value)}
                >
                  {BOOKING_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ minWidth: { md: 220 }, flex: { md: '1 1 220px' } }}>
                <InputLabel id="payment-status-label">Payment Status</InputLabel>
                <Select
                  labelId="payment-status-label"
                  value={paymentStatus}
                  label="Payment Status"
                  onChange={(e) => setPaymentStatus(e.target.value)}
                >
                  {PAYMENT_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                sx={{ flex: { md: '1 1 220px' } }}
                label="Search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Booking number, subject, or area"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadBookings();
                }}
              />

              <Button className="booking-more-filters" variant="outlined" onClick={() => setFiltersExpanded((value) => !value)}>
                {filtersExpanded ? 'Hide filters' : 'More filters'}
              </Button>

              <FormControl className="booking-advanced-filter" fullWidth sx={{ minWidth: { md: 180 }, flex: { md: '1 1 180px' } }}>
                <InputLabel id="delivery-mode-label">Delivery Mode</InputLabel>
                <Select
                  labelId="delivery-mode-label"
                  value={deliveryMode}
                  label="Delivery Mode"
                  onChange={(e) => setDeliveryMode(e.target.value)}
                >
                  {DELIVERY_MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl className="booking-advanced-filter" fullWidth sx={{ minWidth: { md: 180 }, flex: { md: '1 1 180px' } }}>
                <InputLabel id="curriculum-label">Curriculum</InputLabel>
                <Select
                  labelId="curriculum-label"
                  value={curriculum}
                  label="Curriculum"
                  onChange={(e) => setCurriculum(e.target.value)}
                >
                  {CURRICULUM_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl className="booking-advanced-filter" fullWidth sx={{ minWidth: { md: 180 }, flex: { md: '1 1 180px' } }}>
                <InputLabel id="province-label">Province</InputLabel>
                <Select
                  labelId="province-label"
                  value={province}
                  label="Province"
                  onChange={(e) => setProvince(e.target.value)}
                >
                  {PROVINCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button className="booking-refresh" variant="outlined" onClick={loadBookings} disabled={loading} sx={{ flex: { md: '0 0 auto' } }}>
                Refresh
              </Button>
            </Stack>

            {loading && <LinearProgress sx={{ mt: 2 }} />}
            {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 3 }}>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Awaiting Review</Typography>
                  <Typography variant="h5" fontWeight={800}>{summary.awaiting}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Paid</Typography>
                  <Typography variant="h5" fontWeight={800}>{summary.paid}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Confirmed</Typography>
                  <Typography variant="h5" fontWeight={800}>{summary.confirmed}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Rejected</Typography>
                  <Typography variant="h5" fontWeight={800}>{summary.rejected}</Typography>
                </CardContent>
              </Card>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Session Link Failures</Typography>
                  <Typography variant="h5" fontWeight={800}>{summaryData?.support?.sessionLinkFailures ?? 0}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Awaiting > 24h</Typography>
                  <Typography variant="h5" fontWeight={800}>{summaryData?.support?.pendingTooLong ?? 0}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Tutor Rejected</Typography>
                  <Typography variant="h5" fontWeight={800}>{summaryData?.support?.tutorRejected ?? 0}</Typography>
                </CardContent>
              </Card>
              <Card variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Failed Payments</Typography>
                  <Typography variant="h5" fontWeight={800}>{summaryData?.support?.failedPayments ?? 0}</Typography>
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Tutor Requests & Bookings"
            subheader={`${bookings.length} record${bookings.length === 1 ? '' : 's'} loaded`}
          />
          <Divider />
          <CardContent sx={{ p: 0, display: { xs: 'none', lg: 'block' } }}>
            <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Booking</TableCell>
                    <TableCell>Parent</TableCell>
                    <TableCell>Student</TableCell>
                    <TableCell>Tutor</TableCell>
                    <TableCell>Lesson</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Tutor Decision</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            No bookings match the current filters.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : null}

                  {bookings.map((booking) => {
                    return (
                      <TableRow key={booking._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>{booking.bookingNumber}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.subject} · {booking.curriculum} · {formatGradeLabel(booking.student?.grade ?? booking.grade)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatPerson(booking.parent)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.parent?.email || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatPerson(booking.student)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(booking.student?.profileReadiness?.readyForMatching ? 'Ready to match' : 'Profile incomplete')}
                            {' · '}
                            {booking.packageName || booking.student?.packageName || 'No package'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {booking.student?.province || booking.province || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatPerson(booking.selectedTutor)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.selectedTutor ? 'Tutor chosen by parent' : 'No tutor selected yet'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDeliveryMode(booking.deliveryMode)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.hoursRequested} hour{Number(booking.hoursRequested) === 1 ? '' : 's'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={statusLabel(booking.bookingStatus)} color={statusColor(booking.bookingStatus)} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={statusLabel(booking.paymentStatus)} color={statusColor(booking.paymentStatus)} />
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={statusLabel(booking.tutorDecisionStatus || 'pending')} color={statusColor(booking.tutorDecisionStatus)} />
                        </TableCell>
                        <TableCell>{formatDate(booking.createdAt)}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="outlined" onClick={(event) => openActionMenu(event, booking)}>
                            Actions
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
          <CardContent sx={{ display: { xs: 'block', lg: 'none' }, p: 1.5 }}>
            <Stack spacing={1.25}>
              {!loading && bookings.length === 0 ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No bookings match the current filters.</Typography>
                </Box>
              ) : null}
              {bookings.map((booking) => {
                const canReview =
                  ['pending_admin_confirmation', 'tutor_response_overdue'].includes(booking.bookingStatus) &&
                  ['paid_mock', 'paid_manual', 'paid_gateway'].includes(booking.paymentStatus) &&
                  booking.tutorDecisionStatus !== 'rejected';
                const canMarkPaid =
                  ['pending', 'submitted_manual', 'failed'].includes(booking.paymentStatus) &&
                  !['confirmed', 'rejected'].includes(booking.bookingStatus);
                return (
                  <Card key={booking._id} variant="outlined">
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={800} noWrap>{booking.bookingNumber}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                              {booking.subject} · {booking.curriculum} · {formatGradeLabel(booking.student?.grade ?? booking.grade)}
                            </Typography>
                          </Box>
                          <Button size="small" variant="outlined" onClick={(event) => openActionMenu(event, booking)}>Actions</Button>
                        </Stack>
                        <Typography variant="body2"><strong>Parent:</strong> {formatPerson(booking.parent)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{booking.parent?.email || '-'}</Typography>
                        <Typography variant="body2"><strong>Student:</strong> {formatPerson(booking.student)}</Typography>
                        <Typography variant="body2"><strong>Tutor:</strong> {formatPerson(booking.selectedTutor)}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDeliveryMode(booking.deliveryMode)} · {booking.hoursRequested} hour{Number(booking.hoursRequested) === 1 ? '' : 's'} · {booking.student?.province || booking.province || '-'}</Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={statusLabel(booking.bookingStatus)} color={statusColor(booking.bookingStatus)} />
                          <Chip size="small" label={statusLabel(booking.paymentStatus)} color={statusColor(booking.paymentStatus)} />
                          {canReview || canMarkPaid ? <Chip size="small" variant="outlined" label="Action required" /> : null}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Menu anchorEl={actionMenu.anchorEl} open={Boolean(actionMenu.anchorEl)} onClose={closeActionMenu}>
        <MenuItem onClick={() => handleMenuAction('view')}>View booking</MenuItem>
        <MenuItem disabled={!actionMenu.booking || !['pending', 'submitted_manual', 'failed'].includes(actionMenu.booking.paymentStatus) || ['confirmed', 'rejected'].includes(actionMenu.booking.bookingStatus)} onClick={() => handleMenuAction('markPaid')}>Mark paid</MenuItem>
        <MenuItem disabled={!actionMenu.booking || !['pending_admin_confirmation', 'tutor_response_overdue'].includes(actionMenu.booking.bookingStatus) || !['paid_mock', 'paid_manual', 'paid_gateway'].includes(actionMenu.booking.paymentStatus) || actionMenu.booking.tutorDecisionStatus === 'rejected'} onClick={() => handleMenuAction('confirm')}>Confirm booking</MenuItem>
        <MenuItem disabled={!actionMenu.booking || !['pending_admin_confirmation', 'tutor_response_overdue'].includes(actionMenu.booking.bookingStatus) || !['paid_mock', 'paid_manual', 'paid_gateway'].includes(actionMenu.booking.paymentStatus) || actionMenu.booking.tutorDecisionStatus === 'rejected'} onClick={() => handleMenuAction('reject')}>Reject booking</MenuItem>
      </Menu>

      <Dialog open={actionDialog.open} onClose={closeAction} fullWidth maxWidth="sm">
        <DialogTitle>
          {actionDialog.type === 'confirm' ? 'Confirm Tutor Request' : null}
          {actionDialog.type === 'reject' ? 'Reject Tutor Request' : null}
          {actionDialog.type === 'markPaid' ? 'Mark Payment As Paid' : null}
          {actionDialog.type === 'markPending' ? 'Return Payment To Pending' : null}
          {actionDialog.type === 'markFailed' ? 'Mark Payment As Failed' : null}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {actionDialog.booking?.bookingNumber || '-'} · {actionDialog.booking?.subject || '-'} · {formatPerson(actionDialog.booking?.selectedTutor)}
            </Typography>
            {actionDialog.type === 'markPaid' ? (
              <TextField
                fullWidth
                label="Payment Reference"
                value={actionReference}
                onChange={(e) => setActionReference(e.target.value)}
                placeholder="Bank or transfer reference"
              />
            ) : null}
            {actionDialog.type === 'markPaid' ? (
              <FormControl fullWidth>
                <InputLabel id="action-method-label">Payment Method</InputLabel>
                <Select
                  labelId="action-method-label"
                  value={actionMethod}
                  label="Payment Method"
                  onChange={(e) => setActionMethod(e.target.value)}
                >
                  <MenuItem value="manual_transfer">Manual Transfer</MenuItem>
                  <MenuItem value="eft">EFT</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="card_manual">Card Manual</MenuItem>
                </Select>
              </FormControl>
            ) : null}
            {actionDialog.type === 'markPaid' ? (
              <FormControl fullWidth>
                <InputLabel id="action-evidence-label">Evidence Source</InputLabel>
                <Select
                  labelId="action-evidence-label"
                  value={actionEvidenceSource}
                  label="Evidence Source"
                  onChange={(e) => setActionEvidenceSource(e.target.value)}
                >
                  <MenuItem value="bank_reference">Bank Reference</MenuItem>
                  <MenuItem value="proof_of_payment">Proof Of Payment</MenuItem>
                  <MenuItem value="cash_confirmation">Cash Confirmation</MenuItem>
                  <MenuItem value="card_confirmation">Card Confirmation</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            ) : null}
            <TextField
              multiline
              minRows={3}
              fullWidth
              label={
                actionDialog.type === 'confirm'
                  ? 'Confirmation Note'
                  : actionDialog.type === 'markPaid'
                    ? 'Payment Note'
                    : actionDialog.type === 'markPending'
                      ? 'Pending Note'
                      : actionDialog.type === 'markFailed'
                        ? 'Failure Reason'
                        : 'Rejection Reason'
              }
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
              placeholder={
                actionDialog.type === 'confirm'
                  ? 'Approved by admin and ready for LMS scheduling'
                  : actionDialog.type === 'markPaid'
                    ? 'Payment received and verified by admin'
                    : actionDialog.type === 'markPending'
                      ? 'State why payment is back in pending review'
                      : actionDialog.type === 'markFailed'
                        ? 'State why payment failed verification'
                        : 'State why the tutor request is being rejected'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color={
              actionDialog.type === 'confirm' || actionDialog.type === 'markPaid'
                ? 'success'
                : actionDialog.type === 'markPending'
                  ? 'warning'
                  : 'error'
            }
            onClick={submitAction}
            disabled={submitting}
          >
            {actionDialog.type === 'confirm' ? 'Confirm Request' : null}
            {actionDialog.type === 'reject' ? 'Reject Request' : null}
            {actionDialog.type === 'markPaid' ? 'Mark Paid' : null}
            {actionDialog.type === 'markPending' ? 'Mark Pending' : null}
            {actionDialog.type === 'markFailed' ? 'Mark Failed' : null}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentLinkDialog.open} onClose={closePaymentLinkDialog} fullWidth maxWidth="sm">
        <DialogTitle>Create Payment Link</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {paymentLinkDialog.booking?.bookingNumber || '-'} · {paymentLinkDialog.booking?.subject || '-'}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="payment-link-provider-label">Provider</InputLabel>
              <Select
                labelId="payment-link-provider-label"
                value={paymentLinkProvider}
                label="Provider"
                onChange={(e) => changePaymentLinkProvider(e.target.value)}
              >
                <MenuItem value="yoco">Yoco - Local ZAR</MenuItem>
                <MenuItem value="paypal">PayPal - International USD</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label={`Amount (${paymentLinkProvider === 'paypal' ? 'USD' : 'ZAR'})`}
              type="number"
              value={paymentLinkAmount}
              onChange={(e) => setPaymentLinkAmount(e.target.value)}
              inputProps={{ min: '0.01', step: '0.01' }}
              helperText={
                paymentLinkProvider === 'paypal'
                  ? 'PayPal links are created in USD.'
                  : 'Yoco links are created in ZAR.'
              }
            />
            <TextField
              multiline
              minRows={3}
              fullWidth
              label="Admin Note"
              value={paymentLinkNote}
              onChange={(e) => setPaymentLinkNote(e.target.value)}
              placeholder="Optional note for this payment link"
            />
            {paymentLinkError ? <Alert severity="error">{paymentLinkError}</Alert> : null}
            {latestPaymentLink ? (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={800}>Payment link created</Typography>
                <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all', mt: 0.5 }}>
                  {latestPaymentLink}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => copyPaymentLink(latestPaymentLink)}
                >
                  Copy Link
                </Button>
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentLinkDialog} disabled={paymentLinkSubmitting}>
            {latestPaymentLink ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={submitPaymentLink}
            disabled={paymentLinkSubmitting || Boolean(latestPaymentLink)}
          >
            {paymentLinkSubmitting ? 'Creating...' : 'Create Link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={detailOpen} onClose={closeDetail}>
        <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={800}>Booking Detail</Typography>
            {detailLoading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : detailBooking ? (
              <>
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="body2" color="text.secondary">Booking Number</Typography>
                      <Typography variant="body1" fontWeight={700}>{detailBooking.bookingNumber}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip size="small" label={statusLabel(detailBooking.bookingStatus)} color={statusColor(detailBooking.bookingStatus)} />
                        <Chip size="small" label={statusLabel(detailBooking.paymentStatus)} color={statusColor(detailBooking.paymentStatus)} />
                        <Chip size="small" label={`Tutor: ${statusLabel(detailBooking.tutorDecisionStatus || 'pending')}`} color={statusColor(detailBooking.tutorDecisionStatus)} />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Parent</Typography>
                      <Typography variant="body2">{formatPerson(detailBooking.parent)}</Typography>
                      <Typography variant="caption" color="text.secondary">{detailBooking.parent?.email || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">{detailBooking.parent?.phone || '-'}</Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Student Profile</Typography>
                      <Typography variant="body2">{formatPerson(detailBooking.student)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatGradeLabel(detailBooking.student?.grade ?? detailBooking.grade)} · {detailBooking.curriculum}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(detailBooking.student?.schoolName || 'School not provided')}
                        {detailBooking.student?.curriculumLevel ? ` · ${detailBooking.student.curriculumLevel}` : ''}
                        {detailBooking.student?.curriculumSubLevel ? ` · ${detailBooking.student.curriculumSubLevel}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Package: {detailBooking.student?.packageName || detailBooking.packageName || 'Not assigned'}
                        {detailBooking.student?.packageSessionsRemaining != null
                          ? ` · Sessions left ${detailBooking.student.packageSessionsRemaining}`
                          : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Matching readiness: {detailBooking.student?.profileReadiness?.completionPercent ?? 0}% complete
                        {detailBooking.student?.profileReadiness?.readyForMatching ? ' · Ready' : ' · Incomplete'}
                      </Typography>
                      {!detailBooking.student?.profileReadiness?.readyForMatching && (detailBooking.student?.profileReadiness?.missingFields || []).length ? (
                        <Typography variant="caption" color="warning.main">
                          Missing: {detailBooking.student.profileReadiness.missingFields.join(', ')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Matching Profile</Typography>
                      <Typography variant="body2">
                        Subject focus: {detailBooking.subject}
                        {detailBooking.student?.subjects?.length ? ` · ${detailBooking.student.subjects.join(', ')}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDeliveryMode(detailBooking.student?.deliveryMode || detailBooking.deliveryMode)}
                        {' · '}
                        {detailBooking.student?.preferredLessonDuration || detailBooking.hoursRequested} hour{Number(detailBooking.student?.preferredLessonDuration || detailBooking.hoursRequested) === 1 ? '' : 's'}
                        {' · '}
                        {detailBooking.student?.preferredLessonFrequency || 'Frequency not set'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Language: {detailBooking.student?.preferredLanguage || 'Not set'}
                        {' · '}
                        Learning style: {detailBooking.student?.learningStyle || 'Not set'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tutor preference: {detailBooking.student?.preferredTutorGender || 'No gender preference'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Teaching styles: {formatList(detailBooking.student?.preferredTeachingStyles)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Learning support: {formatList(detailBooking.student?.learningSupportNeeds)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Special needs: {formatList(detailBooking.student?.specialNeedsSupport)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Availability: {formatAvailability(detailBooking.student?.availabilitySchedule)}
                      </Typography>
                      {detailBooking.student?.academicGoals ? (
                        <Typography variant="caption" color="text.secondary">
                          Academic goals: {detailBooking.student.academicGoals}
                        </Typography>
                      ) : null}
                      {detailBooking.student?.notes ? (
                        <Typography variant="caption" color="text.secondary">
                          Notes: {detailBooking.student.notes}
                        </Typography>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Tutor</Typography>
                      <Typography variant="body2">{formatPerson(detailBooking.selectedTutor)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {detailBooking.selectedTutor?.email || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Decision: {detailBooking.tutorDecisionStatus || 'pending'} · {formatDate(detailBooking.tutorDecisionAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Note: {detailBooking.tutorDecisionNote || '-'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Lesson</Typography>
                      <Typography variant="body2">{detailBooking.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDeliveryMode(detailBooking.deliveryMode)} · {detailBooking.hoursRequested} hour{Number(detailBooking.hoursRequested) === 1 ? '' : 's'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {detailBooking.student?.addressLine1 || detailBooking.addressLine1 || '-'}, {detailBooking.student?.suburb || detailBooking.suburb || '-'}, {detailBooking.student?.town || detailBooking.town || '-'}, {detailBooking.student?.province || detailBooking.province || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Requested start: {formatDate(detailBooking.requestedStartAt)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                {(detailBooking.subjectAllocations || []).length ? (
                  <Card variant="outlined">
                    <CardContent>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" fontWeight={700}>Subject Distribution</Typography>
                        {detailBooking.subjectAllocations.map((allocation) => (
                          <Box key={allocation._id || allocation.subject} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1} flexWrap="wrap">
                              <Typography variant="body2" fontWeight={800}>{allocation.subject}</Typography>
                              <Chip size="small" label={statusLabel(allocation.tutorDecisionStatus || allocation.status)} color={statusColor(allocation.tutorDecisionStatus || allocation.status)} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Sessions: {allocation.sessionCount || '-'} · Duration: {allocation.hoursPerSession || detailBooking.hoursRequested} hour{Number(allocation.hoursPerSession || detailBooking.hoursRequested) === 1 ? '' : 's'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Tutor: {allocation.selectedTutor?.name || formatPerson(allocation.selectedTutorId) || 'Not assigned'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Requested start: {formatDate(allocation.requestedStartAt)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Session link: {statusLabel(allocation.sessionLinkStatus || 'not_created')}
                            </Typography>
                            {allocation.tutorDecisionNote ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Tutor note: {allocation.tutorDecisionNote}
                              </Typography>
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Finance View</Typography>
                      <Typography variant="body2">Client rate / hour: {formatAdminMoney(detailBooking.pricingSnapshot?.clientRate, detailBooking.pricingSnapshot?.currency)}</Typography>
                      <Typography variant="body2">Tutor remuneration / hour: {formatAdminMoney(detailBooking.pricingSnapshot?.tutorRate, detailBooking.pricingSnapshot?.currency)}</Typography>
                      <Typography variant="body2">Client total: {formatAdminMoney(detailBooking.pricingSnapshot?.totalClientAmount, detailBooking.pricingSnapshot?.currency)}</Typography>
                      <Typography variant="body2">Tutor remuneration total: {formatAdminMoney(detailBooking.pricingSnapshot?.totalTutorAmount, detailBooking.pricingSnapshot?.currency)}</Typography>
                      <Typography variant="body2">Commission / margin: {formatAdminMoney(detailBooking.pricingSnapshot?.commissionAmount, detailBooking.pricingSnapshot?.currency)}</Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" fontWeight={700}>Admin Decision</Typography>
                      <Typography variant="body2">{detailBooking.adminDecisionNote || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Decision at: {formatDate(detailBooking.adminDecisionAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Decision by: {formatPerson(detailBooking.adminDecisionBy)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={700}>Payment History</Typography>
                      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'grey.50' }}>
                        <Stack spacing={1}>
                          <Typography variant="body2" fontWeight={800}>Create Payment Link</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Generate a Yoco ZAR link for local bookings or a PayPal USD link for international bookings.
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => openPaymentLinkDialog(detailBooking)}
                            disabled={!canCreatePaymentLink(detailBooking)}
                            sx={{ alignSelf: 'flex-start' }}
                          >
                            Create Payment Link
                          </Button>
                          {!canCreatePaymentLink(detailBooking) ? (
                            <Typography variant="caption" color="text.secondary">
                              Payment links are disabled for paid, confirmed, rejected, or cancelled bookings.
                            </Typography>
                          ) : null}
                        </Stack>
                      </Box>
                      {(detailBooking.payments || []).length ? (
                        detailBooking.payments.map((payment) => (
                          <Box key={payment._id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="body2" fontWeight={700}>{payment.reference}</Typography>
                              <Chip size="small" label={statusLabel(payment.status)} color={statusColor(payment.status)} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {payment.currency} {payment.amount} · {payment.provider}
                            </Typography>
                            {payment.providerReference ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Provider reference: {payment.providerReference}
                              </Typography>
                            ) : null}
                            {payment.paymentMethod ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Method: {payment.paymentMethod}
                              </Typography>
                            ) : null}
                            <Typography variant="caption" color="text.secondary" display="block">
                              Requested: {formatDate(payment.requestedAt)}
                            </Typography>
                            {payment.submittedAt ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Submitted: {formatDate(payment.submittedAt)}
                              </Typography>
                            ) : null}
                            {payment.confirmedAt ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Confirmed: {formatDate(payment.confirmedAt)}
                              </Typography>
                            ) : null}
                            {payment.failedAt ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Failed: {formatDate(payment.failedAt)}
                              </Typography>
                            ) : null}
                            {payment.statusReason ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Note: {payment.statusReason}
                              </Typography>
                            ) : null}
                            {payment.evidence?.evidenceSource ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Evidence source: {statusLabel(payment.evidence.evidenceSource)}
                              </Typography>
                            ) : null}
                            {payment.evidence?.evidenceCheckedAt ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Evidence checked: {formatDate(payment.evidence.evidenceCheckedAt)}
                              </Typography>
                            ) : null}
                            {payment.evidence?.evidenceCheckedByEmail ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Checked by: {payment.evidence.evidenceCheckedByEmail}
                              </Typography>
                            ) : null}
                            {payment.evidence?.evidenceNote ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Evidence note: {payment.evidence.evidenceNote}
                              </Typography>
                            ) : null}
                            {paymentLinkForPayment(payment) ? (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-all' }}>
                                  Payment link: {paymentLinkForPayment(payment)}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  sx={{ mt: 1 }}
                                  onClick={() => copyPaymentLink(paymentLinkForPayment(payment))}
                                >
                                  Copy Link
                                </Button>
                              </Box>
                            ) : null}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No payment history yet.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => openAction('markPaid', detailBooking)}
                        disabled={detailBooking.bookingStatus === 'confirmed' || detailBooking.bookingStatus === 'rejected'}
                      >
                        Mark Paid
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => openAction('markPending', detailBooking)}
                        disabled={detailBooking.bookingStatus === 'confirmed'}
                      >
                        Mark Pending
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => openAction('markFailed', detailBooking)}
                        disabled={detailBooking.bookingStatus === 'confirmed'}
                      >
                        Mark Failed
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={700}>Tutor Shortlist</Typography>
                      {(detailBooking.assignmentHistory || []).length ? (
                        detailBooking.assignmentHistory.map((entry) => (
                          <Box key={entry._id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" spacing={1}>
                              <Typography variant="body2" fontWeight={700}>{formatPerson(entry.tutor)}</Typography>
                              <Chip size="small" label={entry.state} color={entry.state === 'selected' ? 'success' : 'default'} />
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Rank {entry.rank || '-'} · Match score {entry.matchScore ?? '-'}
                            </Typography>
                            {entry.rationale ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {entry.rationale}
                              </Typography>
                            ) : null}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No shortlist history recorded.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={700}>Timeline</Typography>
                      {(detailBooking.timeline || []).length ? (
                        detailBooking.timeline.map((entry, index) => (
                          <Box key={`${entry.type}-${entry.at}-${index}`} sx={{ borderLeft: '2px solid', borderColor: timelineTone(entry.tone), pl: 2, py: 0.5 }}>
                            <Typography variant="body2" fontWeight={700}>{timelineLabel(entry)}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatDate(entry.at)}
                            </Typography>
                            {entry.meta ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {entry.meta}
                              </Typography>
                            ) : null}
                            {entry.note ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {entry.note}
                              </Typography>
                            ) : null}
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">No timeline entries recorded.</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </>
            ) : null}
          </Stack>
        </Box>
      </Drawer>
    </DashboardLayout>
  );
};

export default Bookings;
